import {
  Directive,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Inject,
  Renderer2,
  ChangeDetectorRef,
  input
} from '@angular/core';
import * as _ from 'lodash';
import { TableColumnManager, TableColumnSettings } from '../services/table-column-manager';
import {
  TableColumnGroup,
  TableColumnGroups,
  TableColumnOption,
  TableColumnPickerData,
  TableColumnSection,
  TableColumnToggle
} from '../models/table-column-option.model';

@Directive({
  standalone: false,
  selector: '[tableColumnManager]'
})
export class TableColumnManagerDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('tableColumnManager') uniqueId!: string;
  @Input() defaultWidths!: { [key: string]: number };
  @Input() settings!: TableColumnSettings;
  /** When false the header is left alone — no resize handles, no drag-reorder, no injected stylesheet. */
  @Input() resizable: boolean = true;
  /** For the header cells' app-table-column-insert-button: the settings groups and keys whose columns can be inserted (when false) or hidden. */
  readonly columnGroups = input<TableColumnGroups>({});
  @Output() settingsChange = new EventEmitter<TableColumnSettings>();

  private manager!: TableColumnManager<any>;
  private observer: MutationObserver | null = null;

  constructor(
    @Inject(ElementRef) private el: ElementRef,
    @Inject(Renderer2) private renderer: Renderer2,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    if (!this.resizable) {
      return;
    }
    if (!this.uniqueId) {
      console.warn('TableColumnManagerDirective: uniqueId is missing or empty.');
    }
    this.manager = new TableColumnManager(
      this.uniqueId,
      this.defaultWidths,
      this.settings,
      (updatedSettings) => {
        this.settingsChange.emit(updatedSettings);
        this.cdr.detectChanges();
      },
      this.renderer
    );
    this.manager.init(this.el.nativeElement);

    this.setupMutationObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.settings && this.manager) {
      this.manager.updateSettings(this.settings);
    }
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.manager) {
      this.manager.destroy();
    }
  }

  /** What the "+" on a column's header cell offers: hidden columns to insert after it, and the column itself when it can be hidden. */
  public getPickerData(group: string, key?: string): TableColumnPickerData {
    const columnGroup = this.columnGroups()[group];
    return {
      sections: this.buildHiddenColumnSections(),
      currentColumn: key && columnGroup?.columnIds[key] && this.settings?.[group]?.[key] ? this.toColumnOption(columnGroup, group, key) : null
    };
  }

  /** Shows a column right after the anchor column (first, when there's no anchor key), or hides one. */
  public toggleColumn({ option, enabled }: TableColumnToggle, anchorGroup: string, anchorKey?: string): void {
    this.settingsChange.emit({
      ...this.settings,
      [option.group]: { ...this.settings[option.group], [option.key]: enabled },
      ...(enabled ? { columnOrder: this.insertIntoColumnOrder(option, anchorGroup, anchorKey) } : {})
    });
  }

  private insertIntoColumnOrder(option: TableColumnOption, anchorGroup: string, anchorKey?: string): string[] {
    const order = this.settings.columnOrder || [];
    const anchorIds = anchorKey ? _.castArray(this.columnGroups()[anchorGroup]?.columnIds[anchorKey] || []) : [];
    const insertIndex = (_.max(anchorIds.map((id: string) => order.indexOf(id))) ?? -1) + 1;
    const insertedIds = _.castArray(this.columnGroups()[option.group].columnIds[option.key]).filter((id: string) => !order.includes(id));
    return [...order.slice(0, insertIndex), ...insertedIds, ...order.slice(insertIndex)];
  }

  private buildHiddenColumnSections(): TableColumnSection[] {
    const sections: TableColumnSection[] = [];
    _.forEach(this.columnGroups(), (columnGroup: TableColumnGroup, group: string) => {
      const options: TableColumnOption[] = _.keys(columnGroup.columnIds)
        .filter((key: string) => this.settings?.[group]?.[key] === false)
        .map((key: string) => this.toColumnOption(columnGroup, group, key));
      if (options.length) {
        sections.push({ title: columnGroup.title, options });
      }
    });
    return sections;
  }

  private toColumnOption(columnGroup: TableColumnGroup, group: string, key: string): TableColumnOption {
    return { group, key, labelKey: columnGroup.labelPrefix + key };
  }

  private setupMutationObserver(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      let hasHeaderChanges = false;
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Check added nodes
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i];
            if (node.nodeType === 1) {
              const element = node as HTMLElement;
              if (element.classList?.contains('th') || element.classList?.contains('td') || element.querySelector?.('.th, .td')) {
                hasHeaderChanges = true;
                break;
              }
            }
          }
          if (hasHeaderChanges) break;

          // Check removed nodes
          for (let i = 0; i < mutation.removedNodes.length; i++) {
            const node = mutation.removedNodes[i];
            if (node.nodeType === 1) {
              const element = node as HTMLElement;
              if (element.classList?.contains('th') || element.classList?.contains('td') || element.querySelector?.('.th, .td')) {
                hasHeaderChanges = true;
                break;
              }
            }
          }
          if (hasHeaderChanges) break;
        }
      }

      if (hasHeaderChanges && this.manager) {
        this.observer?.disconnect();

        this.manager.reinit(this.el.nativeElement);

        this.observer?.observe(this.el.nativeElement, {
          childList: true,
          subtree: true
        });
      }
    });

    this.observer.observe(this.el.nativeElement, {
      childList: true,
      subtree: true
    });
  }
}
